import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Empty,
  Grid,
  Input,
  Pagination,
  Select,
  Skeleton,
  Space,
} from '@arco-design/web-react';
import { IconPlus, IconSearch } from '@arco-design/web-react/icon';
import { PageHeader, VirtualCard } from '@/components/ui';
import { cardsApi } from '@/api';
import type { CardLifecycleStatus, VccCard } from '@/types/card';
import { CardStatusMeta } from '@/types/card';
import ApplyCardModal from './ApplyCardModal';
import CardDetailDrawer from './CardDetailDrawer';

const { Row, Col } = Grid;

const statusOptions: Array<{ label: string; value: CardLifecycleStatus | 'ALL' }> = [
  { label: '全部状态', value: 'ALL' },
  ...(Object.keys(CardStatusMeta) as CardLifecycleStatus[]).map((key) => ({
    value: key,
    label: CardStatusMeta[key].label,
  })),
];

export default function CardWalletView() {
  const [list, setList] = useState<VccCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [status, setStatus] = useState<CardLifecycleStatus | 'ALL'>('ALL');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [activeCard, setActiveCard] = useState<VccCard | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await cardsApi.list({
        page,
        pageSize,
        status: status === 'ALL' ? undefined : status,
        keyword: keyword || undefined,
      });
      setList(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, keyword]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  return (
    <div className="up-page">
      <PageHeader
        title="我的卡包资产"
        subtitle="模拟物理外观的虚拟卡网格视图，点击任意卡片查看详情、调度额度或销卡"
        extra={
          <Button type="primary" icon={<IconPlus />} onClick={() => setApplyOpen(true)}>
            申请新卡
          </Button>
        }
      />

      <div className="up-section-card" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={status}
            style={{ width: 160 }}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          >
            {statusOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
          <Input.Search
            allowClear
            placeholder="搜索别名 / 脱敏卡号"
            prefix={<IconSearch />}
            style={{ width: 280 }}
            onSearch={(v) => {
              setKeyword(v);
              setPage(1);
            }}
          />
        </Space>
      </div>

      {loading ? (
        <Skeleton animation text={{ rows: 6 }} />
      ) : list.length === 0 ? (
        <Empty description="暂无卡片，点击右上角申请新卡" />
      ) : (
        <Row gutter={[16, 16]}>
          {list.map((card) => (
            <Col key={card.id} xs={24} sm={12} md={8} lg={6}>
              <VirtualCard
                alias={card.alias}
                bin={card.bin}
                maskedPan={card.maskedPan}
                expireMonth={card.expireMonth}
                expireYear={card.expireYear}
                status={card.status}
                onClick={() => setActiveCard(card)}
              />
            </Col>
          ))}
        </Row>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showTotal
          sizeCanChange
          onChange={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
        />
      </div>

      <ApplyCardModal
        visible={applyOpen}
        onCancel={() => setApplyOpen(false)}
        onSuccess={() => {
          setApplyOpen(false);
          fetchData();
        }}
      />

      <CardDetailDrawer
        card={activeCard}
        visible={!!activeCard}
        onClose={() => setActiveCard(null)}
        onChanged={() => {
          fetchData();
        }}
      />
    </div>
  );
}
